/**
 * The sync rules: what a flush sends, what it overwrote, and when it must not run
 * at all. Pure — no IndexedDB, no fetch, no React.
 *
 * These are § 4.2–§ 4.4, and they are the rules a manager's typing depends on, so
 * they are testable directly rather than only through a rendered screen.
 *
 * **Last-write-wins, and the writes it won are reported.** Local pending edits beat
 * staging, because the manager holding the iPad is the one who just counted the
 * shelf. But nothing merges silently in either direction: every value this device
 * is about to overwrite is listed, with what staging held and what is going to
 * replace it (§ 4.4). Ownership makes the case rare — a session locks to its owner
 * for entry — and "resume on a different iPad" makes it real.
 *
 * **A session that is no longer editable is never flushed.** Not "flushed and
 * refused" — not attempted. The app says what it is holding and why it cannot send
 * it, and waits for an explicit discard, because silently dropping typing is worse
 * than a dialog.
 */
import type { CountLine, CountSession } from "../api/types";
import type { DraftRecord, SessionRecord } from "./db";

/** Statuses in which staging accepts line changes. Mirrors `sessions/rules.ts`. */
const EDITABLE = ["Open", "InReview", "Error"];

export interface Overwrite {
  item_no: string;
  /** What staging held. `null` means staging had no line — never counted. */
  staged: string | null;
  /** What this device is about to send. `null` means "not counted". */
  local: string | null;
  /** Which device typed the local value, for a resume-on-another-iPad story. */
  device_id: string;
  typed_at: string;
}

export interface FlushPlan {
  /** The entries a full-state `PUT …/lines` should carry. */
  entries: { item_no: string; counted_qty: string | null; unit_of_measure_code?: string }[];
  /** Differences this flush will overwrite, for the reconciliation report. */
  overwrites: Overwrite[];
  /** Drafts that agree with staging already; nothing to send for them. */
  alreadyInStaging: string[];
}

const stagedMap = (lines: CountLine[]) => {
  const staged = new Map<string, string>();
  for (const line of lines) staged.set(line.item_no.trim().toUpperCase(), line.counted_qty);
  return staged;
};

/**
 * What to send, and what it will overwrite.
 *
 * Only **unsynced** drafts are candidates: a `synced` draft is a record of
 * something staging already agreed with, and re-sending it would be noise that
 * could overwrite a *newer* value from another device with an older local one.
 *
 * A draft that matches staging is dropped from the send rather than included.
 * That keeps a flush's size honest — "3 pending" should mean three real
 * differences — and it means a repeated flush after a broken reply converges
 * instead of resending forever.
 */
export function planFlush(drafts: DraftRecord[], stagedLines: CountLine[]): FlushPlan {
  const staged = stagedMap(stagedLines);
  const entries: FlushPlan["entries"] = [];
  const overwrites: Overwrite[] = [];
  const alreadyInStaging: string[] = [];

  for (const draft of drafts) {
    if (draft.sync_state === "synced") continue;

    const stagedQty = staged.get(draft.item_no) ?? null;
    const local = draft.counted_qty;

    // Exact string comparison, deliberately. "7.5" and "7.50" are the same number
    // and this layer does no arithmetic to find that out; the server answers
    // `unchanged` for them, which is the cheap and correct place for that judgement.
    if (stagedQty === local) {
      alreadyInStaging.push(draft.item_no);
      continue;
    }

    // The unit rides with the quantity it was typed in (item 3). Omitted rather
    // than sent as null when the manager never chose one, so a base-unit line is
    // byte-identical on the wire to what it was before item 3 and the staging
    // table keeps applying its own base-UOM default.
    const entry: FlushPlan["entries"][number] = {
      item_no: draft.item_no,
      counted_qty: local,
    };
    if (draft.unit_of_measure_code) {
      entry.unit_of_measure_code = draft.unit_of_measure_code;
    }
    entries.push(entry);

    // An overwrite is only an overwrite if staging actually held something else.
    // A draft over an untouched item is just a new count, and reporting it would
    // bury the handful of real conflicts in a list of ordinary work.
    if (stagedQty !== null) {
      overwrites.push({
        item_no: draft.item_no,
        staged: stagedQty,
        local,
        device_id: draft.device_id,
        typed_at: draft.typed_at,
      });
    }
  }

  return { entries, overwrites, alreadyInStaging };
}

export type FlushBlock =
  | { allowed: true }
  | { allowed: false; reason: "not-editable"; status: string; message: string }
  | { allowed: false; reason: "not-owner"; owner: string; message: string }
  | { allowed: false; reason: "offline"; message: string }
  | { allowed: false; reason: "nothing-pending"; message: string };

/**
 * Whether a flush may run at all — checked before anything is sent.
 *
 * The `not-editable` arm is the one § 4.4 rules on explicitly: **pending drafts
 * are never flushed** into a session staging has frozen. Attempting it would earn
 * a 409 per item and leave a manager reading a wall of refusals about work they
 * cannot rescue anyway; the honest move is to say what is held and offer a
 * discard.
 */
export function canFlush(
  session: CountSession,
  principal: string | null,
  online: boolean,
  pending: number,
): FlushBlock {
  if (pending === 0) {
    return {
      allowed: false,
      reason: "nothing-pending",
      message: "Everything typed on this device is in Business Central.",
    };
  }

  const editable =
    typeof session.editable === "boolean"
      ? session.editable
      : EDITABLE.includes(session.status);

  if (!editable) {
    return {
      allowed: false,
      reason: "not-editable",
      status: session.status,
      message:
        `This count is ${session.status} in Business Central, which no longer accepts ` +
        `changes, so what is held on this device cannot be sent. Nothing has been ` +
        `discarded — read it below and decide.`,
    };
  }

  const owner = (session.responsible_manager ?? "").trim().toLowerCase();
  if (owner && owner !== (principal ?? "").trim().toLowerCase()) {
    return {
      allowed: false,
      reason: "not-owner",
      owner: session.responsible_manager,
      message:
        `${session.responsible_manager} is the responsible manager for this count, and ` +
        `entry locks to its owner, so this device cannot send what it is holding.`,
    };
  }

  if (!online) {
    return {
      allowed: false,
      reason: "offline",
      message:
        "No connection. Everything typed is held on this device and will be sent " +
        "when the connection comes back.",
    };
  }

  return { allowed: true };
}

/**
 * Whether the review screen may open (§ 4.2).
 *
 * S5 is computed entirely from staging, so it must not open over unsent edits: a
 * subtotal that is missing the manager's last five entries is not a slightly stale
 * screen, it is **a screen that lies about money** — and it is the screen the G3
 * sign-off is about.
 *
 * `last_full_sync_at` is reported rather than used as the test. The test is
 * "nothing is pending", which is the thing that actually matters; the timestamp is
 * what the screen shows to explain itself.
 */
export function reviewReadiness(
  pending: number,
  record: SessionRecord | undefined,
): { ready: boolean; pending: number; lastFullSyncAt: string | null; message: string } {
  const lastFullSyncAt = record?.last_full_sync_at ?? null;
  if (pending === 0) {
    return {
      ready: true,
      pending,
      lastFullSyncAt,
      message: lastFullSyncAt
        ? "Every count on this device is in Business Central."
        : "Nothing is held on this device.",
    };
  }
  return {
    ready: false,
    pending,
    lastFullSyncAt,
    message:
      `${pending} count${pending === 1 ? "" : "s"} on this device ${
        pending === 1 ? "has" : "have"
      } not reached Business Central yet. The review screen adds up what BC holds, so ` +
      `it would leave ${pending === 1 ? "that entry" : "those entries"} out of the ` +
      `totals and the swing flags. Sync first.`,
  };
}

/**
 * Whether reopening this session must land on **verifying** (§ 4.5, rule 4).
 *
 * True when a post was attempted and no terminal outcome is recorded. In that state
 * the app **does not offer a Post button** and does not decide anything — it asks
 * BC, because the transport fault breaks the *reply*, not the request, so "we sent
 * it and heard nothing" and "it posted" are indistinguishable from here.
 *
 * `Posting` counts as unresolved: it is BC saying "in progress", not an outcome.
 */
export function needsVerifying(
  session: CountSession,
  record: SessionRecord | undefined,
): boolean {
  if (!record?.post_attempted_at) return false;
  return session.status !== "Posted" && session.status !== "Error";
}

/** Whether a terminal outcome has arrived, so the marker can be cleared. */
export function isTerminal(session: CountSession): boolean {
  return session.status === "Posted" || session.status === "Error";
}
